import { FC, useState, useEffect, Fragment } from "react";
import { useNavigate } from "react-router-dom";
import { actionArr, FilterArr, TableCol } from "./Data";
import { TableLayout } from "Layouts";
import { ControlledTable, Filter2 } from "Components";
import { RouteUrls } from "Constant/RouteUrls";
import PatientApi from "Service/Patient.api";
import Formate from "./Formate";
import { Filter2DataProps } from "DataTypes/Common";
import { useDispatch } from "react-redux";
import { addSelectedPrgm } from "Redux/Patient/Action";
import { selectedPrgmInitialState } from "Redux/Patient/reducer";
import PatientFormatter from "Utils/Patient.Formatter";
import { EnrolledPatientType } from "DataTypes/Services/Patient.type";
import { CreateFilter2Payload } from "Utils/common";
import CommonFormatter from "Utils/Common.Formatter";
import { ReadOnlyType } from "DataTypes/PatientManagement.type";
import Stages from "Constant/Stages";
import { ScreenInitialStateType } from "DataTypes/Redux";
import { useSelector } from "react-redux";
import UI from "Constant/UI";
import { setPageLimit } from "Redux/Screen/Action";

const Enrolled: FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { listPagination }: ScreenInitialStateType = useSelector(
    (state: any) => state?.screen
  );
  const reduxLimit = (listPagination || []).find(
    el => el.id == "enrolled"
  )?.limit || 10;
  const [loading, setLoading] = useState(true);
  const [pageData, setPageData] = useState({
    ...UI.pageData,
    limit: reduxLimit
  });
  const [rows, setRows] = useState<EnrolledPatientType[]>([]);
  const [searchText, setSearchText] = useState("");
  const [filterHeight, setFilterHeight] = useState<number>(0);
  const [filterData, setFilterData] = useState<Filter2DataProps[]>(
    JSON.parse(JSON.stringify(FilterArr()))
  );
  const tableHeight = `calc(99% - ${filterHeight}px)`;

  useEffect(() => {
    handleSetData(1, pageData.limit);
  }, [filterData]);

  const handleSetData: (
    currentPage: number,
    currentLimit: number
  ) => void = async (currentPage, currentLimit) => {
    setLoading(true);
    const newFilterPayload = CreateFilter2Payload(filterData);
    const payload = {
      ...(newFilterPayload || {}),
    };
    const res = await PatientApi.Enrolled({
      offset: (currentPage - 1) * currentLimit,
      limit: currentLimit,
      data: payload,
    });
    let newPageData = { ...UI.pageData }
    let newRows: EnrolledPatientType[] = [];
    if (res?.success) {
      newRows = Formate.EnrolledPatientList(res?.data);
      newPageData = res.criterion;
    }
    setRows(newRows);
    setPageData(newPageData)
    dispatch(setPageLimit({ id: "enrolled", limit: newPageData.limit }));
    setLoading(false);
  };

  const handlePatientDetails: (data: EnrolledPatientType) => void = (data) => {
    navigate(
      `../../${RouteUrls.patient}/${data.patientId}/${RouteUrls.details}`
    );
  };

  const handleChart: (data: EnrolledPatientType) => void = (data) => {
    navigate(
      `../../${RouteUrls.patient}/${data.patientId}` +
      `/${RouteUrls.clinicalSummary}`
    );
  };

  const handleTask: (data: EnrolledPatientType) => void = (data) => {
    navigate(
      `../../${RouteUrls.patient}/${data.patientId}` +
      `/${RouteUrls.task}/${RouteUrls.add}`
    );
  };

  const handleViewCare: (data: EnrolledPatientType) => void = (data) => {
    let careplanState = data.careplanState;
    let careplanStage = PatientFormatter.GetCPStage(careplanState);
    if (data.careplanType == "REVISION") {
      //In revision, super admin can change the team as well from this flow
      //so i am manually setting the state to sign off so the admin can go
      //care team even if care plan is before care team state and in revision,  
      //data is already copied from care plan so we will not get empty values
      careplanState = Stages.CPSignOff;
      careplanStage = PatientFormatter.GetCPStage(Stages.CPSignOff);
    }
    dispatch(
      addSelectedPrgm({
        ...selectedPrgmInitialState,
        careplanType: data.careplanType,
        callDefaultRoute: `../${CommonFormatter.HandleParentRoute({
          prgm: data.program,
          isRev: data.careplanType == "REVISION",
          careId: data.careplanId,
        })}/${PatientFormatter.StateRouteSwitch(data.careplanState)}`,
        careplanState,
        careplanStage,
        careplanId: data.careplanId,
        programId: data.programId,
        abbrevation: data.program,
        caremanagerId: data.caremanagerId,
        billableproviderId: data.billableproviderId,
        providerId: data.teamProviderId,
        teamInitiatedBy: data.teamInitiatedBy,
        teamInitiatorRoleId: data.teamInitiatorRoleId,
        eligibleProgramId: data.eligibleProgramId,
        readOnlyArr: [
          "uptoDate",
          "problem",
          "addDevice",
          "editDevice",
          "question",
          "addNotes",
          "frequency",
          "interactive",
          "medically",
          ...data.careplanType == "REVISION" ?
            [] :
            [
              "care-team",
              "add-provider",
              "edit-provider"
            ] as ReadOnlyType[],
        ],
      })
    );
    navigate(
      `../../${RouteUrls.patient}/${data.patientId
      }/${CommonFormatter.HandleParentRoute({
        prgm: data.program,
        isRev: data.careplanType == "REVISION",
        careId: data.careplanId,
      })}/${PatientFormatter.StateRouteSwitch(data.careplanState)}`
    );
  };

  const TblBodyJSX = (
    <ControlledTable.TblBody
      tableColumn={() => TableCol()}
      tableRow={rows}
      tableAction={(data: EnrolledPatientType) =>
        actionArr(
          () => handleChart(data),
          () => handlePatientDetails(data),
          () => handleTask(data),
          () => handleViewCare(data),
          data.program
        )
      }
      searchText={searchText}
    />
  );

  return (
    <Fragment>
      <Filter2
        filterDataArr={filterData}
        handleFilterClick={(data) => setFilterData(data)}
        changeSearchText={(val) => setSearchText(val)}
        setFilterHeight={(val) => setFilterHeight(val)}
      />
      <TableLayout
        tblBody={TblBodyJSX}
        tblHead={ControlledTable.TblHead(TableCol())}
        height={tableHeight}
        loading={loading}
        pageRecordCount={rows.length}
        totalRecordCount={pageData.total}
        Pagination={
          <ControlledTable.Pagination
            mainPage={pageData.page}
            limit={pageData.limit}
            total={pageData.total}
            handleClick={handleSetData}
            tableStyle={true}
          />
        }
      />
    </Fragment>
  );
};
export default Enrolled;
